import {
  IDataList,
  IPhase,
  IPhaseItem,
  IUploadedFilesData,
  IUserMe,
  IFileUploadingUtilityProps,
  ClientPlatformEnum,
} from 'redux-store/types';
import {
  ICostCode,
  ISelectionItem,
  IUpdatedSelectionItemMeta,
  TApprovedSelectionItemMeta,
  TDeletedSelectionItemMeta,
} from '../selections';
import { IProfile } from '../profiles';
import { ICreateActivityFeedHashtag, IGetActivityFeedHashtag } from './hashtags.types';

export enum ActivityFeedStatusEnum {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
}

export interface IUploadingFiles extends IUploadedFilesData, IFileUploadingUtilityProps {}

export interface IPostUploadsOptions {
  soundLevel: number[];
  duration: string;
  previewUrl?: string;
}

export enum UploadStatusEnum {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export interface IPostUploads extends IUploadedFilesData {
  path: string;
  mimeType: string;
  description: string | null;
  location?: string | null;
  timestamp?: string | null;
  options: IPostUploadsOptions | null;
  status: UploadStatusEnum;
}

export interface ICommentUploads extends IUploadedFilesData {
  mimeType: string;
  description: string | null;
  location?: string | null;
  timestamp?: string | null;
  status: UploadStatusEnum;
}

export interface IActivityFeedProfile {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  role: string;
  avatar: string | null;
}

export interface IMention {
  profileId: number;
  length: number;
  offset: number;
  block: number;
  key: number;
}

export interface IPostPhase {
  id: number;
  title: string;
  color: string;
}

export interface IPostPhaseItem {
  id: number;
  title: string;
}

export interface IPostUploadedFiles {
  id: string;
  description: string | null;
  location?: string | null;
  timestamp?: string | null;
}

export const enum PostVisibilityEnum {
  EVERYONE = 'EVERYONE',
  JUST_ME = 'JUST_ME',
  SPECIFIC_MEMBERS = 'SPECIFIC_MEMBERS',
}

export enum ActivityFeedPostTypeEnum {
  POST = 'POST',
  EVENT = 'EVENT',
}

export enum ActivityFeedPostEventsEnum {
  PHASE_ITEM_COMPLETED = 'PHASE_ITEM_COMPLETED',
  PHASE_ITEM_RETURNED_TO_PROGRESS = 'PHASE_ITEM_RETURNED_TO_PROGRESS',
  SELECTION_ITEM_APPROVED = 'SELECTION_ITEM_APPROVED',
  SELECTION_ITEM_UNAPPROVED = 'SELECTION_ITEM_UNAPPROVED',
}

export interface IActivityFeedPhaseItemEvent {
  type:
    | ActivityFeedPostEventsEnum.PHASE_ITEM_COMPLETED
    | ActivityFeedPostEventsEnum.PHASE_ITEM_RETURNED_TO_PROGRESS;
  profileId: number;
  phaseItemId: number;
  profile: Pick<IProfile, 'id' | 'userId' | 'firstName' | 'lastName' | 'role' | 'avatar'>;
  phaseItem: {
    phase: {
      id: number;
      title: string;
    };
    id: number;
    title: string;
  };
}

export interface IActivityFeedSelectionEvent {
  type:
    | ActivityFeedPostEventsEnum.SELECTION_ITEM_APPROVED
    | ActivityFeedPostEventsEnum.SELECTION_ITEM_UNAPPROVED;
  profileId: number;
  selectionOptionId: number;
  profile: Pick<IProfile, 'id' | 'userId' | 'firstName' | 'lastName' | 'role' | 'avatar'>;
  selectionOption: {
    id: number;
    logo: IUploadedFilesData | null;
    selectionItemId: number;
    title: string;
  };
}

export interface IActivityFeedEvent {
  type: ActivityFeedPostTypeEnum.EVENT;
  id: number;
  status: 'PUBLISHED' | 'DRAFT';
  createdAt: string;
  publishedAt: string;
  event: IActivityFeedPhaseItemEvent | IActivityFeedSelectionEvent;
}

export interface IActivityFeedPost {
  type: ActivityFeedPostTypeEnum.POST;
  id: number;
  createdAt: string;
  publishedAt: string | null;
  isViewed: boolean;
  isLiked: boolean;
  likesCount: number;
  commentsCount: number;
  data: {
    hashtagMentions: IGetActivityFeedHashtag[];
    mentions: IMention[];
    activityFeedItemId: number;
    content: string | null;
    phases: IPostPhase[];
    comments: IActivityFeedComment[];
    phaseItems: IPostPhaseItem[];
    uploads: IPostUploads[];
    profile: IActivityFeedProfile;
  };
  postVisibility: PostVisibilityEnum;
  status: ActivityFeedStatusEnum;
}

export interface ICreatePostAudienceBody {
  projectId: number;
  group: {
    groupName: string;
    profileIds: number[];
    isVisible: boolean;
  };
}

export interface IActivityFeedComment {
  id: number;
  postId: number;
  activityFeedItemId: number;
  createdAt: string;
  publishedAt: string | null;
  content: string | null;
  uploads: ICommentUploads[];
  isLiked: boolean;
  likesCount: number;
  isView: boolean;
  profile: IActivityFeedProfile;
  replyToCommentId: number | null;
  rootCommentId: number | null;
  replyToComment: {
    id: number;
    content: string | null;
    profile: IActivityFeedProfile;
  } | null;
  replies: IActivityFeedComment[];
  mentions: IMention[];
  hashtagMentions: IGetActivityFeedHashtag[];
  status: ActivityFeedStatusEnum;
}

export interface IActivityFeedAudience {
  profileId: number;
  firstName: string;
  lastName: string;
  roleName: string;
  avatar: string;
}

export interface IActivityFeedLike {
  id: number;
  activityFeedItemId: number;
  profile: {
    id: number;
    firstName: string;
    lastName: string;
    avatar: string;
  };
}

export interface IActivityFeedUnviewedItemsCount {
  notifications?: number;
  posts?: number;
  messages?: number;
}

export interface IActivityFeedItemsArgs {
  projectId: number;
  cursor?: number;
  limit?: number;
  search?: string;
  fromDate?: string;
  toData?: string;
  isFileIncluded?: boolean;
  isPhotoIncluded?: boolean;
  phasesIds?: number[];
  withEvents?: boolean;
  phaseItemsIds?: number[];
  posterProfileIds?: number[];
}

export interface ICreatePostBody {
  content: string;
  projectId: number;
  phaseIds?: number[];
  phaseItemIds?: number[];
  uploads?: IPostUploadedFiles[];
  audienceGroups?: number[];
  audienceRoles?: number[];
  postVisibility: PostVisibilityEnum;
  mentions: IMention[];
  hashtagMentions: ICreateActivityFeedHashtag[];
  visibleProfileIds?: number[];
  status?: ActivityFeedStatusEnum;
}

export interface IUpdatePostBody {
  id: number;
  projectId: number;
  content: string;
  uploads?: IPostUploadedFiles[];
  phaseIds?: number[];
  phaseItemIds?: number[];
  audienceGroups?: number[];
  audienceRoles?: number[];
  postVisibility: PostVisibilityEnum;
  hashtagMentions?: ICreateActivityFeedHashtag[];
  mentions?: IMention[];
  visibleProfileIds?: number[];
  status?: ActivityFeedStatusEnum;
  wasPreviouslyDraft?: boolean;
}

export interface IDeletePostsBody {
  ids: number[];
  projectId: number;
  status?: ActivityFeedStatusEnum;
}

export interface IGetPostByIdArgs {
  postId: number;
  projectId: number;
}

export interface IGetCommentsArgs {
  postId: number;
  cursor?: number;
  limit?: number;
  isView?: boolean;
}

export interface ICreateCommentBody {
  postId: number;
  content: string | null;
  replyToCommentId?: number | null;
  rootCommentId?: number | null;
  uploads?: string[];
  mentions: IMention[];
  hashtagMentions: ICreateActivityFeedHashtag[];
  status?: ActivityFeedStatusEnum;
  projectId: number;
  addLocal?: boolean;
}

export interface IUpdateCommentBody {
  id: number;
  content: string | null;
  projectId: number;
  uploads?: string[];
  mentions: IMention[];
  hashtagMentions: ICreateActivityFeedHashtag[];
  status?: ActivityFeedStatusEnum;
  postId: number;
  wasPreviouslyDraft?: boolean;
}

export interface IDeleteCommentsBody {
  ids: number[];
  postId: number;
  projectId: number;
  rootCommentId: number | null;
  status?: ActivityFeedStatusEnum;
}

export interface IActivityFeedItemLikesArgs {
  activityFeedId: number;
  cursor?: number;
  limit?: number;
}

export interface ILikeActivityFeedItemArgs {
  activityFeedItemId: number;
  projectId: number;
  postId: number;
  commentId?: number;
  rootCommentId?: number;
}

export interface IActivityFeedUnviewedItemsArgs {
  projectId: number;
}

export interface ICreateItemsViewsBody {
  projectId: number;
  itemIds: number[];
  selectionItemIds: number[];
}

export interface ICreateItemsViewsBulkBody {
  projectId: number;
  unviewedCountArgs?: number[];
  withoutUpdateUnviewedCount?: boolean;
}

export interface IGetProjectAudienceArgs {
  projectId: number;
}

export interface IGetPostAudienceArgs {
  projectId: number;
  postId: number;
}

export interface IDeleteCustomAudienceBody {
  projectId: number;
  groupId: number;
  groupIdToAssign: number;
  roleIdToAssign: number;
}

export interface IGetRecentActivityArgs {
  projectId: number;
  isViewed?: boolean;
  cursor?: number;
  limit?: number;
}

export interface ICreateItemsViewsResponse {
  itemIds: number[];
}

export interface INotificationAuthorProfile
  extends Pick<
    IUserMe,
    | 'id'
    | 'firstName'
    | 'lastName'
    | 'phone'
    | 'role'
    | 'userId'
    | 'createdAt'
    | 'deletedAt'
    | 'avatar'
    | 'isActive'
  > {}

export enum ProjectNotificationEventEnum {
  ITEM_OR_PHASE_HAS_BEEN_RESCHEDULED = 'ITEM_OR_PHASE_HAS_BEEN_RESCHEDULED',
  INSPECTION_HAS_PASSED = 'INSPECTION_HAS_PASSED',
  SCHEDULE_ITEM_HAS_BEEN_COMPLETED = 'SCHEDULE_ITEM_HAS_BEEN_COMPLETED',
  PHASE_HAS_BEEN_CHANGED = 'PHASE_HAS_BEEN_CHANGED',
  PHASE_OR_ITEM_TITLE_HAS_CHANGED = 'PHASE_OR_ITEM_TITLE_HAS_CHANGED',
  PHASE_HAS_BEEN_COMPLETED = 'PHASE_HAS_BEEN_COMPLETED',
  NEW_USERS_HAVE_BEEN_ADDED = 'NEW_USERS_HAVE_BEEN_ADDED',
  ITEMS_BECOME_OVERDUE = 'ITEMS_BECOME_OVERDUE',
  ITEM_OVERDUE_STATUS_HAS_CHANGED = 'ITEM_OVERDUE_STATUS_HAS_CHANGED',
  SCHEDULE_ITEM_HAS_BEEN_CREATED = 'SCHEDULE_ITEM_HAS_BEEN_CREATED',
}

export enum SelectionNotificationEventEnum {
  SELECTION_ITEM_HAS_BEEN_ADDED = 'SELECTION_ITEM_HAS_BEEN_ADDED',
  SELECTION_ITEM_BECOME_OVERDUE = 'SELECTION_ITEM_BECOME_OVERDUE',
  SELECTION_ITEM_HAS_BEEN_APPROVED = 'SELECTION_ITEM_HAS_BEEN_APPROVED',
  SELECTION_ITEM_NEEDS_APPROVAL = 'SELECTION_ITEM_NEEDS_APPROVAL',
}

export enum SelectionItemsEventEnum {
  COST_CODES_CATEGORY_UPDATED = 'COST_CODES_CATEGORY_UPDATED',
  COST_CODES_CATEGORY_DELETED = 'COST_CODES_CATEGORY_DELETED',

  SELECTION_ITEMS_CLONED = 'SELECTION_ITEMS_CLONED',
  SELECTION_ITEMS_ADDED = 'SELECTION_ITEMS_ADDED',
  SELECTION_ITEMS_IMPORT_LIBRARY = 'SELECTION_ITEMS_IMPORT_LIBRARY',
  SELECTION_ITEMS_IMPORT_TEMPLATE = 'SELECTION_ITEMS_IMPORT_TEMPLATE',
  SELECTION_ITEMS_DELETED = 'SELECTION_ITEMS_DELETED',
  SELECTION_ITEMS_UPDATED = 'SELECTION_ITEMS_UPDATED',
  SELECTION_ITEMS_APPROVED = 'SELECTION_ITEMS_APPROVED',
  SELECTION_ITEMS_DISAPPROVED = 'SELECTION_ITEMS_DISAPPROVED',
  SELECTION_ITEMS_SET_REMINDER = 'SELECTION_ITEMS_SET_REMINDER',

  COST_CODES_ADDED = 'COST_CODES_ADDED',
  COST_CODES_UPDATED = 'COST_CODES_UPDATED',
  COST_CODES_DELETED = 'COST_CODES_DELETED',
  COST_CODES_IMPORT = 'COST_CODES_IMPORT',
}

export interface IProjectNotificationData {
  event: ProjectNotificationEventEnum;
  reasonForChange: string | null;
  newStartDate: string | null;
  newEndDate: string | null;
  oldStartDate: string | null;
  oldEndDate: string | null;
}

export interface ISelectionNotificationData {
  event: SelectionNotificationEventEnum;
  id: number;
  createdAt: string;
  projectSelectionItemId: number;
  projectSelectionItem: {
    id: number;
    categoryId: number;
    endDate: string | null;
    title: string;
  };
}

export type TNotificationData = IProjectNotificationData | ISelectionNotificationData;

export interface INotification {
  id: number;
  projectId: number;
  phase: Pick<IPhase, 'id' | 'title' | 'color'> | null;
  phaseItem: Pick<IPhaseItem, 'id' | 'title'>;
  isViewed: boolean;
  createdAt: string;
  profile: INotificationAuthorProfile;
  data: TNotificationData;
}

export enum SocketNotificationTypeEnum {
  ACTIVITY_FEED = 'ACTIVITY_FEED',
  SELECTION = 'SELECTION',
}

export interface ISocketProjectNotificationData {
  projectId: number;
  phaseId: number | null;
  phaseItemId: number | null;
  profileId: number;
  activityFeedNotificationId: number | null;
  selectionNotificationId: number | null;
  id: number;
  createdAt: string;
  type: SocketNotificationTypeEnum;
}

export interface ISocketProjectNotification
  extends Pick<
    IProjectNotificationData,
    'newEndDate' | 'newStartDate' | 'oldEndDate' | 'oldStartDate' | 'reasonForChange'
  > {
  event: ProjectNotificationEventEnum;
  id: number;
  phaseName?: string;
  phaseItemName?: string;
  newItem?: IPhaseItem;
  notification: ISocketProjectNotificationData;
  isOverdueItemCompleted?: boolean;
}

export interface ISocketSelectionNotification {
  id: number;
  projectSelectionItemId: number;
  createdAt: string;
  event: SelectionNotificationEventEnum;
  selectionItem: ISelectionItem;
}

export type TSocketNotification = ISocketProjectNotification | ISocketSelectionNotification;

export interface IRecentPost {
  type: 'POST';
  id: number;
  createdAt: string;
  isViewed: boolean;
  data: {
    activityFeedItemId: number;
    content: string | null;
    mentions: IMention[];
    hashtagMentions: IGetActivityFeedHashtag[];
    uploads: IPostUploads[];
    profile: IActivityFeedProfile;
    profileId: number;
  };
}

export interface IRecentComment {
  type: 'COMMENT';
  id: number;
  createdAt: string;
  isViewed: boolean;
  data: {
    activityFeedItemId: number;
    content: string | null;
    mentions: IMention[];
    hashtagMentions: IGetActivityFeedHashtag[];
    uploads: ICommentUploads[];
    profile: IActivityFeedProfile;
    profileId: number;
    comment: IActivityFeedComment;
  };
}

export type TRecentActivity = IRecentPost | IRecentComment;

export type TGetRecentActivityResponse = IDataList<TRecentActivity>;
export type TGetNotificationsResponse = IDataList<INotification>;
export type TActivityFeedPosts = IDataList<IActivityFeedPost | IActivityFeedEvent>;
export type TActivityFeedComments = IDataList<IActivityFeedComment>;
export type TActivityFeedItemLikes = IDataList<IActivityFeedLike>;
export interface ISocketPost extends IActivityFeedPost {
  postUserIds: number[];
}

export interface ISocketComment extends IActivityFeedComment {
  userIds: number[];
  postUserIds: number[];
}

export interface ISocketPostLikeData {
  activityFeedItemId: number;
  profileId: number;
}

export interface ISocketCommentLikeData {
  activityFeedItemId: number;
  profileId: number;
  postId: number;
  rootCommentId?: number;
}

export interface ISocketDeletePostData {
  activityFeedItemIds: { activityFeedItemId: number; status: ActivityFeedStatusEnum }[];
  profileId: number;
}

export interface ISocketDeleteCommentData {
  activityFeedItemIds: {
    activityFeedItemId: number;
    postId: number;
    rootCommentId?: number;
    status: ActivityFeedStatusEnum;
  }[];
  deletedCommentsCount: number;
  profileId: number;
}

type ISocketUpdatedUpload = {
  projectId: number;
  profileId: number;
  activityFeedItemId: number;
  uploadId: string;
  uploadStatus: UploadStatusEnum;
  postId: number;
  commentId: number | null;
  rootCommentId: number | null;
  type: 'POST' | 'COMMENT';
};

export type TUpdatedUploadEvent = {
  type: 'UPDATED_UPLOAD';
  projectId: number;
  data: ISocketUpdatedUpload;
};

export type TActivityFeedEvent =
  | TUpdatedUploadEvent
  | { type: 'NEW_POST'; data: ISocketPost; projectId: number }
  | { type: 'NEW_COMMENT'; data: ISocketComment; projectId: number }
  | { type: 'NEW_NOTIFICATION'; data: TSocketNotification; projectId: number }
  | {
      type: 'LIKED_POST';
      data: ISocketPostLikeData;
      projectId: number;
    }
  | {
      type: 'UNLIKED_POST';
      data: ISocketPostLikeData;
      projectId: number;
    }
  | {
      type: 'LIKED_COMMENT';
      data: ISocketCommentLikeData;
      projectId: number;
    }
  | {
      type: 'UNLIKED_COMMENT';
      data: ISocketCommentLikeData;
      projectId: number;
    }
  | { type: 'CHANGED_POST'; data: ISocketPost; projectId: number; profileId: number }
  | { type: 'CHANGED_COMMENT'; data: ISocketComment; projectId: number; profileId: number }
  | {
      type: 'DELETED_POST';
      data: ISocketDeletePostData;
      projectId: number;
    }
  | {
      type: 'DELETED_COMMENT';
      data: ISocketDeleteCommentData;
      projectId: number;
    }
  | {
      type: 'projectCreatorJoinToRoom';
      profileId: number;
      companyId: number;
      projectId: number;
    }
  | {
      type: SelectionItemsEventEnum.SELECTION_ITEMS_CLONED;
      projectId: number;
      profileId: number;
      platform: ClientPlatformEnum;
      data: {
        selectionItems: ISelectionItem[];
      };
    }
  | {
      type: SelectionItemsEventEnum.SELECTION_ITEMS_ADDED;
      projectId: number;
      profileId: number;
      platform: ClientPlatformEnum;
      data: {
        selectionItems: ISelectionItem[];
      };
    }
  | {
      type: SelectionItemsEventEnum.SELECTION_ITEMS_IMPORT_LIBRARY;
      projectId: number;
      profileId: number;
      platform: ClientPlatformEnum;
    }
  | {
      type: SelectionItemsEventEnum.SELECTION_ITEMS_IMPORT_TEMPLATE;
      projectId: number;
      profileId: number;
      platform: ClientPlatformEnum;
    }
  | {
      type: SelectionItemsEventEnum.SELECTION_ITEMS_DELETED;
      projectId: number;
      profileId: number;
      platform: ClientPlatformEnum;
      data: {
        deletedSelectionItemsMeta?: TDeletedSelectionItemMeta[];
      };
    }
  | {
      type: SelectionItemsEventEnum.SELECTION_ITEMS_UPDATED;
      projectId: number;
      profileId: number;
      platform: ClientPlatformEnum;
      data: {
        selectionItems: ISelectionItem[];
        updatedSelectionItemsMeta: IUpdatedSelectionItemMeta[];
      };
    }
  | {
      type: SelectionItemsEventEnum.SELECTION_ITEMS_APPROVED;
      projectId: number;
      profileId: number;
      platform: ClientPlatformEnum;
      data: {
        approvedSelectionItemsMeta?: TApprovedSelectionItemMeta[];
        selectionItems: ISelectionItem[];
      };
    }
  | {
      type: SelectionItemsEventEnum.SELECTION_ITEMS_DISAPPROVED;
      projectId: number;
      profileId: number;
      platform: ClientPlatformEnum;
      data: {
        selectionItems: ISelectionItem[];
      };
    }
  | {
      type: SelectionItemsEventEnum.SELECTION_ITEMS_SET_REMINDER;
      projectId: number;
      profileId: number;
      platform: ClientPlatformEnum;
      data: {
        costCodes: ICostCode[];
      };
    }
  | {
      type: SelectionItemsEventEnum.COST_CODES_UPDATED;
      projectId: number;
      profileId: number;
      platform: ClientPlatformEnum;
      data: {
        costCodes: ICostCode[];
      };
    }
  | {
      type: SelectionItemsEventEnum.COST_CODES_ADDED;
      projectId: number;
      profileId: number;
      platform: ClientPlatformEnum;
      data: {
        costCodes: ICostCode[];
      };
    }
  | {
      type: SelectionItemsEventEnum.COST_CODES_DELETED;
      projectId: number;
      profileId: number;
      platform: ClientPlatformEnum;
      data: {
        costCodesIds: number[];
      };
    }
  | {
      type: SelectionItemsEventEnum.COST_CODES_IMPORT;
      projectId: number;
      profileId: number;
      platform: ClientPlatformEnum;
    }
  | {
      type: SelectionItemsEventEnum.COST_CODES_CATEGORY_DELETED;
      projectId: number;
      profileId: number;
      categoryId: number;
      platform: ClientPlatformEnum;
    }
  | {
      type: SelectionItemsEventEnum.COST_CODES_CATEGORY_UPDATED;
      projectId: number;
      profileId: number;
      categoryId: number;
      name: string;
      number: string;
      platform: ClientPlatformEnum;
    };

export interface ISpecificUsers {
  byRole: {
    id: number;
    groupName: string;
    profiles: IActivityFeedAudience[];
  }[];
  customGroups: {
    id: number;
    groupName: string;
    profiles: IActivityFeedAudience[];
  }[];
  customUsers: IActivityFeedAudience[];
}

export interface ISpecificUsersArgs {
  projectId: number;
}

export interface IGetNotificationsArgs {
  projectId: number;
  limit: number;
  cursor?: number;
}

export interface IGetUnviewedNotificationsArgs {
  projectId: number;
  isViewed?: boolean;
  limit: number;
  cursor?: number;
}

export interface IActivityFeedAppliableFilters {
  phases: number[];
  phaseItems: number[];
  profileIds: number[];
}

export interface IGetActivityFeedAppliableFiltersArgs {
  projectId: number;
}

export interface IGetActivityFeedMediaArgs {
  projectId: number;
  offset?: number;
  limit?: number;
}

export interface IActivityFeedMedia extends Omit<IPostUploads, 'path'> {
  createdAt: Date;
  profile: Pick<IProfile, 'id' | 'userId' | 'role' | 'firstName' | 'lastName' | 'role'> & {
    avatar: IUploadedFilesData;
  };
}

export type TGetActivityFeedMediaResponse = IDataList<IActivityFeedMedia>;

export type TActivityFeedMediaWithIndex = IActivityFeedMedia & { index: number };

export type TActivityFeedMediaMapped = IDataList<[string, TActivityFeedMediaWithIndex[]]>;

export enum ActivityFeedDraftTypeEnum {
  POST = 'POST',
  COMMENT = 'COMMENT',
}

export interface IGetActivityFeedDraftsArgs {
  projectIds?: number[];
  type?: ActivityFeedDraftTypeEnum;
}

export type TActivityFeedPostDraft = Omit<IActivityFeedPost['data'], 'comments'> &
  Pick<IActivityFeedPost, 'postVisibility' | 'id'> & { profileId: number };

export type TActivityFeedCommentDraft = Pick<
  IActivityFeedComment,
  | 'id'
  | 'activityFeedItemId'
  | 'createdAt'
  | 'content'
  | 'postId'
  | 'rootCommentId'
  | 'replyToCommentId'
  | 'profile'
  | 'uploads'
  | 'mentions'
  | 'hashtagMentions'
> & { profileId: number };

interface IActivityFeedDraftShared {
  id: number;
  projectId: number;
  createdAt: string;
  status: ActivityFeedStatusEnum;
}
export interface IActivityFeedDraftPost extends IActivityFeedDraftShared {
  data: TActivityFeedPostDraft;
  type: ActivityFeedDraftTypeEnum.POST;
}

export interface IActivityFeedDraftComment extends IActivityFeedDraftShared {
  data: TActivityFeedCommentDraft;
  type: ActivityFeedDraftTypeEnum.COMMENT;
}

export type TActivityFeedDraftItem = IActivityFeedDraftPost | IActivityFeedDraftComment;

export type TActivityFeedDraftItemUploads = TActivityFeedDraftItem['data']['uploads'];

export type TGetActivityFeedDraftsResponse = IDataList<TActivityFeedDraftItem>;

export interface IActivityFeedEditableDraftPost extends IActivityFeedDraftShared {
  data: TActivityFeedPostDraft;
  postVisibility: PostVisibilityEnum;
  type: ActivityFeedDraftTypeEnum.POST;
}

export interface IActivityFeedEditableDraftComment extends TActivityFeedCommentDraft {
  type: ActivityFeedDraftTypeEnum.COMMENT;
  status: ActivityFeedStatusEnum;
  projectId: number;
}
